/**
 * MEYBRA Hype Mode — procedural background beats via Web Audio API.
 *
 * Why synthesis instead of bundled MP3s?
 *  - 100% royalty-free (we generated it on the fly)
 *  - Zero storage, zero CDN bytes, works offline
 *  - BPM/intensity adapts per vibe preset
 *
 * Vibes:
 *  - HYPE  → 128 BPM EDM-ish kick + offbeat hihat + bass pulse
 *  - FOCUS → 80  BPM lo-fi: soft kick + jazz hat + mellow bass
 *  - PUMP  → 95  BPM hip-hop: kick + snare backbeat + 808 sub
 *
 * Each preset schedules beats one bar at a time via setTimeout so we can
 * cleanly pause/resume without re-creating the AudioContext.
 */

const PRESETS = {
  HYPE: {
    label: 'HYPE',
    description: 'High-energy EDM pulse',
    bpm: 128,
    color: '#ef4444',
    pattern: 'edm',
  },
  FOCUS: {
    label: 'FOCUS',
    description: 'Lo-fi chill loop',
    bpm: 80,
    color: '#3b82f6',
    pattern: 'lofi',
  },
  PUMP: {
    label: 'PUMP',
    description: 'Hip-hop backbeat',
    bpm: 95,
    color: '#daae3e',
    pattern: 'hiphop',
  },
};

const playKick = (ctx, dest, time, gain = 1) => {
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.frequency.setValueAtTime(150, time);
  osc.frequency.exponentialRampToValueAtTime(40, time + 0.18);
  g.gain.setValueAtTime(0.9 * gain, time);
  g.gain.exponentialRampToValueAtTime(0.001, time + 0.25);
  osc.connect(g);
  g.connect(dest);
  osc.start(time);
  osc.stop(time + 0.3);
};

const playHat = (ctx, dest, time, gain = 1, soft = false) => {
  // Noise burst through highpass for hi-hat
  const bufferSize = ctx.sampleRate * 0.05;
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;

  const noise = ctx.createBufferSource();
  noise.buffer = buffer;

  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = soft ? 4000 : 7000;

  const g = ctx.createGain();
  g.gain.setValueAtTime((soft ? 0.12 : 0.25) * gain, time);
  g.gain.exponentialRampToValueAtTime(0.001, time + (soft ? 0.08 : 0.05));

  noise.connect(hp);
  hp.connect(g);
  g.connect(dest);
  noise.start(time);
  noise.stop(time + 0.1);
};

const playSnare = (ctx, dest, time, gain = 1) => {
  // Tone body
  const osc = ctx.createOscillator();
  osc.type = 'triangle';
  osc.frequency.value = 200;
  const og = ctx.createGain();
  og.gain.setValueAtTime(0.4 * gain, time);
  og.gain.exponentialRampToValueAtTime(0.001, time + 0.12);
  osc.connect(og);
  og.connect(dest);
  osc.start(time);
  osc.stop(time + 0.15);

  // Noise crack
  const bufferSize = ctx.sampleRate * 0.15;
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
  const noise = ctx.createBufferSource();
  noise.buffer = buffer;
  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 1500;
  const ng = ctx.createGain();
  ng.gain.setValueAtTime(0.35 * gain, time);
  ng.gain.exponentialRampToValueAtTime(0.001, time + 0.14);
  noise.connect(hp);
  hp.connect(ng);
  ng.connect(dest);
  noise.start(time);
  noise.stop(time + 0.15);
};

const playBass = (ctx, dest, time, freq = 55, duration = 0.4, gain = 1) => {
  const osc = ctx.createOscillator();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(freq, time);
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.setValueAtTime(400, time);
  lp.frequency.exponentialRampToValueAtTime(120, time + duration);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0, time);
  g.gain.linearRampToValueAtTime(0.25 * gain, time + 0.01);
  g.gain.exponentialRampToValueAtTime(0.001, time + duration);
  osc.connect(lp);
  lp.connect(g);
  g.connect(dest);
  osc.start(time);
  osc.stop(time + duration + 0.05);
};

const scheduleBar = (ctx, dest, startTime, preset) => {
  const secondsPerBeat = 60 / preset.bpm;
  const t = (beat) => startTime + beat * secondsPerBeat;

  if (preset.pattern === 'edm') {
    // 4-on-the-floor kick + offbeat hat + bass on 1 & 3
    for (let i = 0; i < 4; i++) playKick(ctx, dest, t(i), 1);
    for (let i = 0; i < 4; i++) playHat(ctx, dest, t(i + 0.5), 0.9);
    playBass(ctx, dest, t(0), 55, secondsPerBeat * 0.8);
    playBass(ctx, dest, t(2), 55, secondsPerBeat * 0.8);
  } else if (preset.pattern === 'lofi') {
    // Soft kick 1 & 3, brushed hat 1/2/3/4, mellow bass
    playKick(ctx, dest, t(0), 0.7);
    playKick(ctx, dest, t(2), 0.7);
    for (let i = 0; i < 4; i++) playHat(ctx, dest, t(i), 0.6, true);
    playBass(ctx, dest, t(0), 49, secondsPerBeat * 1.8, 0.7);
    playBass(ctx, dest, t(2.5), 55, secondsPerBeat * 1.4, 0.7);
  } else if (preset.pattern === 'hiphop') {
    // Kick on 1 & 3 (with ghost on 2.75), snare on 2 & 4, 808 bass
    playKick(ctx, dest, t(0), 1);
    playKick(ctx, dest, t(2.75), 0.6);
    playKick(ctx, dest, t(3), 0.9);
    playSnare(ctx, dest, t(1));
    playSnare(ctx, dest, t(3));
    for (let i = 0; i < 8; i++) playHat(ctx, dest, t(i * 0.5), 0.7);
    playBass(ctx, dest, t(0), 41, secondsPerBeat * 1.8);
    playBass(ctx, dest, t(2.5), 49, secondsPerBeat * 1.2);
  }
};

class HypeEngine {
  constructor() {
    this.ctx = null;
    this.masterGain = null;
    this.timer = null;
    this.preset = null;
    this.running = false;
    this.volume = 0.4;
  }

  _ensureCtx() {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = this.volume;
      // Soft limiter via dynamics compressor — keeps peaks tame
      const comp = this.ctx.createDynamicsCompressor();
      comp.threshold.value = -10;
      comp.ratio.value = 4;
      this.masterGain.connect(comp);
      comp.connect(this.ctx.destination);
    }
  }

  start(vibe) {
    this.stop();
    const preset = PRESETS[vibe];
    if (!preset) return;
    this._ensureCtx();
    if (this.ctx.state === 'suspended') this.ctx.resume();

    this.preset = preset;
    this.running = true;

    const barSeconds = (60 / preset.bpm) * 4;
    let nextBarTime = this.ctx.currentTime + 0.1;

    const loop = () => {
      if (!this.running) return;
      // Schedule the next bar ahead
      scheduleBar(this.ctx, this.masterGain, nextBarTime, preset);
      nextBarTime += barSeconds;
      // Wake up shortly before the next bar to schedule the one after
      this.timer = setTimeout(loop, (barSeconds - 0.2) * 1000);
    };
    loop();
  }

  stop() {
    this.running = false;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  pause() {
    if (this.ctx && this.ctx.state === 'running') this.ctx.suspend();
  }

  resume() {
    if (this.ctx && this.ctx.state === 'suspended') this.ctx.resume();
  }

  setVolume(v) {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.masterGain) {
      this.masterGain.gain.setTargetAtTime(this.volume, this.ctx.currentTime, 0.05);
    }
  }

  destroy() {
    this.stop();
    if (this.ctx) {
      this.ctx.close().catch(() => {});
      this.ctx = null;
      this.masterGain = null;
    }
  }
}

// =============================================
// Motivational coach phrases (TTS)
// =============================================

const PHRASES = {
  start: [
    "Let's go! Beast mode on.",
    "Lock in. You got this.",
    "All right, time to work.",
    "Game time. Let's move.",
  ],
  midWork: [
    "Push harder.",
    "Don't slow down.",
    "Stay strong.",
    "Keep your form.",
    "You're built for this.",
    "Eyes up, breathe.",
  ],
  finalStretch: [
    "Last 10 seconds, give it all!",
    "Finish strong!",
    "Empty the tank!",
    "Ten seconds left, go!",
  ],
  rest: [
    "Recover. Breathe deep.",
    "Catch your breath.",
    "Reset. Stay focused.",
    "Shake it out, you got this.",
  ],
  setStart: [
    "Set {n}. Let's go!",
    "Round {n}. Bring it!",
    "Set {n}. No quit.",
    "Round {n}. Stay sharp.",
  ],
  drillComplete: [
    "Nice work. Next one.",
    "That's how it's done.",
    "Solid. Move it.",
    "On to the next.",
  ],
  sessionComplete: [
    "Session complete. You earned it.",
    "That's a wrap. Strong work.",
    "Done. You showed up today.",
  ],
};

const randomPhrase = (key, replacements = {}) => {
  const pool = PHRASES[key] || [];
  if (!pool.length) return '';
  let phrase = pool[Math.floor(Math.random() * pool.length)];
  Object.entries(replacements).forEach(([k, v]) => {
    phrase = phrase.replace(`{${k}}`, v);
  });
  return phrase;
};

export { HypeEngine, PRESETS, randomPhrase };
